import * as XLSX from 'xlsx';
import { MeetingInfo, Activity, Participant } from '../models/types';
import {
  getCellValue, 
  getCellDateValue, 
  findCellWithText,
} from '../utils/excelUtils';

/**
 * Service for parsing calendar Excel files and extracting meeting information
 */
export class CalendarParserService {
  /**
   * Parses an Excel file and extracts meeting information
   * @param file The Excel file as File object or ArrayBuffer
   * @returns A MeetingInfo object with the extracted data
   */
  public async parseExcelFile(file: File | ArrayBuffer): Promise<MeetingInfo> {
    // Read the Excel file
    const data = file instanceof File 
      ? await this.readFileAsArrayBuffer(file) 
      : file;
    
    const workbook = XLSX.read(data, { type: 'array' });
    
    // Find the relevant sheets
    const summarySheet = workbook.Sheets[workbook.SheetNames[0]]; // First sheet is usually the summary
    const scheduleSheet = this.findSheetByName(workbook, 'INTERNAL SCHEDULE');
    
    // Extract meeting information
    const meetingInfo = this.extractMeetingInfo(summarySheet);
    
    // Extract activities
    const activities = this.extractActivitiesFromSheet(summarySheet, scheduleSheet);
    
    // Extract participants
    const participants = this.extractParticipantsFromSheet(scheduleSheet);
    
    // Combine all information and ensure required fields are present
    const result: MeetingInfo = {
      title: meetingInfo.title || 'Untitled Meeting',
      meetingDate: meetingInfo.meetingDate || new Date(),
      activities,
      participants,
      endDate: meetingInfo.endDate,
      description: meetingInfo.description,
      comments: meetingInfo.comments
    };
    
    // Validate the extracted data
    this.validateCalendarData(result);
    
    return result;
  }
  
  /**
   * Reads a File as ArrayBuffer
   * @param file The file to read
   * @returns Promise resolving to ArrayBuffer
   */
  private readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as ArrayBuffer);
      reader.onerror = (e) => reject(e);
      reader.readAsArrayBuffer(file);
    });
  }
  
  /**
   * Finds a sheet by name
   * @param workbook The workbook
   * @param name The sheet name to find
   * @returns The worksheet or undefined if not found
   */
  private findSheetByName(workbook: XLSX.WorkBook, name: string): XLSX.WorkSheet | undefined {
    const sheetName = workbook.SheetNames.find((n: string) => n.includes(name));
    return sheetName ? workbook.Sheets[sheetName] : undefined;
  }
  
  /**
   * Extracts basic meeting information from the summary sheet
   * @param sheet The summary sheet
   * @returns Partial MeetingInfo object
   */
  private extractMeetingInfo(sheet: XLSX.WorkSheet): Partial<MeetingInfo> {
    // Look for IC Meeting rows
    const icMeetingCell = findCellWithText(sheet, 'IC Meeting');
    
    if (!icMeetingCell) {
      // If no IC Meeting found, look for FC Q rows
      const fcCell = findCellWithText(sheet, 'FC Q');
      
      if (!fcCell) {
        throw new Error('Could not find meeting information in the Excel file');
      }
      
      // Extract FC meeting info
      const { r, c } = XLSX.utils.decode_cell(fcCell);
      const titleCell = XLSX.utils.encode_cell({ r, c });
      const startDateCell = XLSX.utils.encode_cell({ r, c: c + 2 });
      const endDateCell = XLSX.utils.encode_cell({ r, c: c + 3 });
      const commentsCell = XLSX.utils.encode_cell({ r, c: c + 4 });
      
      return {
        title: getCellValue(sheet, titleCell)?.toString() || 'FC Meeting',
        meetingDate: getCellDateValue(sheet, startDateCell) as Date,
        endDate: getCellDateValue(sheet, endDateCell) as Date,
        description: getCellValue(sheet, commentsCell)?.toString(),
      };
    }
    
    // Extract IC meeting info
    const { r, c } = XLSX.utils.decode_cell(icMeetingCell);
    const titleCell = XLSX.utils.encode_cell({ r, c });
    const startDateCell = XLSX.utils.encode_cell({ r, c: c + 2 });
    const endDateCell = XLSX.utils.encode_cell({ r, c: c + 3 });
    const commentsCell = XLSX.utils.encode_cell({ r, c: c + 4 });
    
    return {
      title: getCellValue(sheet, titleCell)?.toString() || 'IC Meeting',
      meetingDate: getCellDateValue(sheet, startDateCell) as Date,
      endDate: getCellDateValue(sheet, endDateCell) as Date,
      description: getCellValue(sheet, commentsCell)?.toString(),
    };
  }
  
  /**
   * Extracts activities from the sheets
   * @param summarySheet The summary sheet
   * @param scheduleSheet The schedule sheet (optional)
   * @returns Array of activities
   */
  private extractActivitiesFromSheet(summarySheet: XLSX.WorkSheet, scheduleSheet?: XLSX.WorkSheet): Activity[] {
    const activities: Activity[] = [];
    
    // First try to extract from the summary sheet
    const range = XLSX.utils.decode_range(summarySheet['!ref'] || 'A1:A1');
    
    for (let row = range.s.r; row <= range.e.r; row++) {
      const titleCell = XLSX.utils.encode_cell({ r: row, c: 0 });
      const startCommentCell = XLSX.utils.encode_cell({ r: row, c: 1 });
      const startDateCell = XLSX.utils.encode_cell({ r: row, c: 2 });
      const endDateCell = XLSX.utils.encode_cell({ r: row, c: 3 });
      const commentsCell = XLSX.utils.encode_cell({ r: row, c: 4 });
      
      const title = getCellValue(summarySheet, titleCell);
      const startDate = getCellDateValue(summarySheet, startDateCell);
      
      if (title && startDate instanceof Date) {
        const activity: Activity = {
          title: title.toString(),
          startDate,
          description: getCellValue(summarySheet, commentsCell)?.toString(),
          comments: getCellValue(summarySheet, startCommentCell)?.toString()
        };
        
        const endDate = getCellDateValue(summarySheet, endDateCell);
        if (endDate instanceof Date) {
          activity.endDate = endDate;
        }
        
        activities.push(activity);
      }
    }
    
    // If we have a schedule sheet, extract more detailed activities
    if (scheduleSheet) {
      const scheduleActivities = this.extractActivitiesFromScheduleSheet(scheduleSheet);
      activities.push(...scheduleActivities);
    }
    
    return activities;
  }
  
  /**
   * Extracts activities from the schedule sheet
   * @param sheet The schedule sheet
   * @returns Array of activities
   */
  private extractActivitiesFromScheduleSheet(sheet: XLSX.WorkSheet): Activity[] {
    const activities: Activity[] = [];
    const activityCell = findCellWithText(sheet, 'Activity');
    
    if (activityCell) {
      const { r, c } = XLSX.utils.decode_cell(activityCell);
      const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1:A1');
      
      // Start from the row after the header
      for (let row = r + 1; row <= range.e.r; row++) {
        const titleCell = XLSX.utils.encode_cell({ r: row, c });
        const title = getCellValue(sheet, titleCell);
        
        if (title) {
          // Look for date cells in this row
          let startDate: Date | undefined;
          
          // Check cells to the right for dates
          for (let dateCol = c + 2; dateCol <= range.e.c; dateCol++) {
            const dateCell = XLSX.utils.encode_cell({ r: row, c: dateCol });
            const dateValue = getCellDateValue(sheet, dateCell);
            
            if (dateValue instanceof Date) {
              startDate = dateValue;
              break;
            }
          }
          
          // Look for description in the next column
          const descCell = XLSX.utils.encode_cell({ r: row, c: c + 1 });
          const description = getCellValue(sheet, descCell)?.toString();
          
          if (startDate) {
            activities.push({
              title: title.toString(),
              startDate,
              description
            });
          }
        }
      }
    }
    
    return activities;
  }
  
  /**
   * Extracts participants from the schedule sheet
   * @param scheduleSheet The schedule sheet (optional)
   * @returns Array of participants
   */
  private extractParticipantsFromSheet(scheduleSheet?: XLSX.WorkSheet): Participant[] {
    if (!scheduleSheet) {
      return [];
    }
    
    const participants: Participant[] = [];
    const responsibleCell = findCellWithText(scheduleSheet, 'Responsible');
    const involvedCell = findCellWithText(scheduleSheet, 'Involved');
    
    if (responsibleCell) {
      const { r, c } = XLSX.utils.decode_cell(responsibleCell);
      const range = XLSX.utils.decode_range(scheduleSheet['!ref'] || 'A1:A1');
      
      // Extract participants from the responsible column
      for (let row = r + 1; row <= range.e.r; row++) {
        const nameCell = XLSX.utils.encode_cell({ r: row, c });
        const name = getCellValue(scheduleSheet, nameCell);
        
        if (name) {
          const names = name.toString().split(',').map((n: string) => n.trim());
          
          names.forEach((n: string) => {
            if (n && !participants.some(p => p.name === n)) {
              participants.push({
                name: n,
                role: 'Responsible'
              });
            }
          });
        }
      }
    }
    
    if (involvedCell) {
      const { r, c } = XLSX.utils.decode_cell(involvedCell);
      const range = XLSX.utils.decode_range(scheduleSheet['!ref'] || 'A1:A1');
      
      // Extract participants from the involved column
      for (let row = r + 1; row <= range.e.r; row++) {
        const nameCell = XLSX.utils.encode_cell({ r: row, c });
        const name = getCellValue(scheduleSheet, nameCell);
        
        if (name) {
          const names = name.toString().split(',').map((n: string) => n.trim());
          
          names.forEach((n: string) => {
            if (n && !participants.some(p => p.name === n)) {
              participants.push({
                name: n,
                role: 'Participant'
              });
            }
          });
        }
      }
    }
    
    return participants;
  }
  
  /**
   * Validates the extracted calendar data
   * @param data The extracted data
   * @throws Error if data is invalid
   */
  private validateCalendarData(data: Partial<MeetingInfo>): void {
    if (!data.title) {
      throw new Error('Meeting title is missing');
    }
    
    if (!data.meetingDate) {
      throw new Error('Meeting date is missing');
    }
    
    if (!data.activities || data.activities.length === 0) {
      throw new Error('No activities found');
    }
  }
}
